// TableSettings.js
import React, { useState, useContext } from "react";
import TableSettingModal from "./TableSettingModal";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import "./styles/PropertiesComponent.css";
import CommonButton from "../elments/CommonButton";
import Loader from "./Loader";
import VariableContext from "../contexts/VariableContext";
import ConvertTypeModal from "./ConvertTypeModal";
import { duplicateVariable, SPECIAL_ROWS } from "../utils/conversions";

const PropertiesComponent = ({
  selectedVariable,
  setdataGroup,
  dataGroup,
  selectedKey,
  setSelectedVariable,
  projectId,nets, setNets
}) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const { selectedProject, selectedClient, powner } = useContext(VariableContext);
  const [activeTab, setActiveTab] = useState("properties");
  const [netLabel, setNetLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  // One-step undo for type conversions: snapshot of dataGroup before the last apply.
  const [convSnapshot, setConvSnapshot] = useState(null);

  // All possible boolean properties with default values.
  // Note: netswithingroups is handled in the dedicated Ranking section below — not listed here.
  const allBooleanProperties = {
    removeTab: false,
    mean: false,
    median: false,
    removeIndividual: false,
    summaryNet: false,
    grid: false,
    removerows: false,
    szr: false,
    reverseGrid: false,
    addNets: false,
    saveToExcel: false,
    downloadDVExcel: false,
    SPSSSyntax: false,
    conceptTables: false,
    reverseLocation: false,
    dataCuts: false,
    SE: false,
    STD: false,
    allstats: false,
    showNetswithinLabels: false,
  };

  const TOGGLE_LABELS = {
    removeTab:            "Remove Tab",
    mean:                 "Mean",
    median:               "Median",
    removeIndividual:     "Remove Individual",
    summaryNet:           "Summary Net",
    grid:                 "Grid",
    removerows:           "Remove Rows",
    szr:                  "SZR",
    reverseGrid:          "Reverse Grid",
    addNets:              "Add Nets",
    saveToExcel:          "Save to Excel",
    downloadDVExcel:      "DV Excel",
    SPSSSyntax:           "SPSS Syntax",
    conceptTables:        "Concept Tables",
    reverseLocation:      "Reverse Location",
    dataCuts:             "Data Cuts",
    SE:                   "Std Error",
    STD:                  "Std Dev",
    allstats:             "All Stats",
    showNetswithinLabels: "Nets in Labels",
  };

  // All possible text properties with default values
  const allTextProperties = {
    combo: "",
    flttb: "",
    stacked: "",
    nets: "",
    type: selectedVariable?.type || "",
    table: selectedVariable?.table || "freq",
    Qtitle: selectedVariable?.Qtitle || "",
    basetitle: selectedVariable?.basetitle || "All respondents",
    tabSpectitle: selectedVariable?.tabSpectitle || selectedVariable?.Qtitle || "",
    Logic: selectedVariable?.Logic || "",
    weightVar: selectedVariable?.weightVar || ""
    
  };

  if (!selectedVariable) {
    return (
      <div className="variable-properties">
        <h2>No variable selected</h2>
        <p>Please select a variable from the list to view its properties.</p>
      </div>
    );
  }

  // Helper to get property value (exists? use it, otherwise use default).
  // Also handles known case-variant aliases so old data with different casing is
  // still surfaced by the correct canonical key (e.g. removetab → removeTab).
  const PROPERTY_ALIASES = { removeTab: "removetab" };
  const getPropertyValue = (property, defaultValue = false) => {
    if (selectedVariable.hasOwnProperty(property)) return selectedVariable[property];
    const alias = PROPERTY_ALIASES[property];
    if (alias && selectedVariable.hasOwnProperty(alias)) return selectedVariable[alias];
    return defaultValue;
  };

  // Helper to set property. When a canonical property has a known alias, the alias
  // key is removed so the variable never holds both forms simultaneously.
  const setPropertyValue = (property, value) => {
    setSelectedVariable((prev) => {
      const next = { ...prev, [property]: value };
      const alias = PROPERTY_ALIASES[property];
      if (alias) delete next[alias];
      return next;
    });
  };

  const handleSaveCustomVariable = async () => {
    const isCustomType = selectedVariable?.type === 'Custom';
    const isMergeType  = !isCustomType && selectedVariable?.type === 'Multiple2DGrid';
    const isSingleGridType = !isCustomType && selectedVariable?.type === 'Single2DGrid';
    const hasCustomRows = (selectedVariable?.customRows?.length || 0) > 0;
    if (!selectedVariable || (!isCustomType && !hasCustomRows)) return;

    const customRows = selectedVariable.customRows || [];
    const rowsWithLogic = customRows.filter(r => r.logic && r.logic.trim());

    if (rowsWithLogic.length === 0) {
      toast.warning("No custom rows with logic defined. Please add custom rows with logic first.");
      return;
    }

    // For merge into existing Multiple2DGrid: block on duplicate indices before confirming
    if (isMergeType) {
      const existingIndices = new Set((selectedVariable.Rows || []).map(r => String(r.index)));
      const duplicates = rowsWithLogic.filter(cr => existingIndices.has(String(cr.index)));
      if (duplicates.length > 0) {
        toast.error(
          `Duplicate row index(es) found: ${duplicates.map(d => d.index).join(', ')}. ` +
          `Please change the custom row indices before saving.`
        );
        return;
      }
    }

    // For Single2DGrid: the custom row index becomes a value code inside the
    // grid data, so block indices that already exist as answer codes
    if (isSingleGridType) {
      const existingCodes = new Set(
        (selectedVariable.values || []).map(v => String(parseInt(v.index)))
      );
      const duplicates = rowsWithLogic.filter(cr => existingCodes.has(String(parseInt(cr.index))));
      if (duplicates.length > 0) {
        toast.error(
          `Value code(s) already used by this grid: ${duplicates.map(d => d.index).join(', ')}. ` +
          `Please change the custom row indices before saving.`
        );
        return;
      }
    }

    const confirmMessage = isCustomType
      ? `This will create ${rowsWithLogic.length} binary (Yes/No) variables in SPSS — one per custom row — and convert this question to Multiple2DGrid format. Rows without logic will be skipped. Proceed?`
      : isMergeType
        ? `This will add ${rowsWithLogic.length} new row(s) to "${selectedKey}" (Multiple2DGrid) and create the corresponding binary SPSS variables. Rows without logic will be skipped. Proceed?`
        : isSingleGridType
          ? `This will write code(s) ${rowsWithLogic.map(r => r.index).join(', ')} into the data of all ${(selectedVariable.varName || []).length} grid variable(s) of "${selectedKey}" wherever each row's condition is met, and add the value label(s) at the end. WARNING: any existing answer of a qualified respondent will be REPLACED by the new code. The custom rows will then be removed. Rows without logic will be skipped. Proceed?`
          : `This will create a new variable "${selectedKey}new" as a Multiple2DGrid with ${rowsWithLogic.length} binary (Yes/No) variable(s) from the custom rows. The original variable will have its custom rows removed. Rows without logic will be skipped. Proceed?`;

    const confirmed = await confirm({
      title: isSingleGridType ? "Save Custom Rows into Grid Data" : "Save Custom Variable as Multiple2DGrid",
      message: confirmMessage,
      confirmText: "Save Variable",
    });
    if (!confirmed) return;

    setLoading(true);
    try {
      if (isSingleGridType) {
        // Single2DGrid: write each custom row's index as a value code into every
        // grid variable (based on its condition) and append the value label —
        // the variable stays a Single2DGrid, no new SPSS variables are created.
        const { data } = await apiService.post('/save-single2dgrid-custom-values', {
          projectId,
          key: selectedKey,
          customRows: rowsWithLogic,
          varName: selectedVariable.varName || [],
          dataGroup,
          client: JSON.stringify(selectedClient || {}),
          project: JSON.stringify(selectedProject || {}),
          user: powner || '',
        });

        if (!data.success) {
          toast.error("Error: " + (data.error || "Unknown error"));
          return;
        }

        const appliedRows = data.applied || [];
        const { customRows: _removed, ...baseVariable } = selectedVariable;

        // Match the key/index style already used by this variable ("99.0" vs "99")
        const usesDotZero = Object.keys(baseVariable.valueLabels || {})
          .some(k => String(k).endsWith('.0'));
        const newValues = [...(baseVariable.values || [])];
        const newValueLabels = { ...(baseVariable.valueLabels || {}) };
        appliedRows.forEach(r => {
          newValues.push({ index: usesDotZero ? `${r.index}.0` : r.index, title: r.label });
          newValueLabels[usesDotZero ? `${r.index}.0` : `${r.index}`] = r.label;
        });

        const updatedVariable = {
          ...baseVariable,
          values: newValues,
          valueLabels: newValueLabels,
        };
        setSelectedVariable(updatedVariable);
        const updatedDataGroup = { ...dataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });

        toast.success(`Variable saved! ${appliedRows.length} value code(s) written into the grid data.`);
        if (data.errors && data.errors.length > 0) {
          toast.warning(`${data.errors.length} row(s) had errors and were skipped.`);
        }
        return;
      }

      const { data } = await apiService.post('/save-custom-as-multiple2dgrid', {
        projectId,
        key: selectedKey,
        customRows: rowsWithLogic,
        dataGroup,
        client: JSON.stringify(selectedClient || {}),
        project: JSON.stringify(selectedProject || {}),
        user: powner || '',
      });

      if (!data.success) {
        toast.error("Error: " + (data.error || "Unknown error"));
        return;
      }

      const newRows = rowsWithLogic.map(cr => {
        const row = { index: cr.index, title: cr.label };
        if (cr.logicBase && cr.logicBase.trim()) row.Logic = cr.logicBase.trim();
        if (cr.norank) row.norank = true;
        if (cr.fix) row.fix = true;
        return row;
      });
      const newValues = [{ index: 0, title: "No" }, { index: 1, title: "Yes" }];
      const newValueLabels = { "0.0": "No", "1.0": "Yes" };

      const { customRows: _removed, ...baseVariable } = selectedVariable;

      if (isCustomType) {
        // Convert the variable itself in-place to Multiple2DGrid
        const updatedVariable = {
          ...baseVariable,
          type: "Multiple2DGrid",
          Rows: newRows,
          values: newValues,
          valueLabels: newValueLabels,
          varName: data.varNames,
        };
        setSelectedVariable(updatedVariable);
        const updatedDataGroup = { ...dataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      } else if (isMergeType) {
        // Merge new rows into the existing Multiple2DGrid variable
        const updatedVariable = {
          ...baseVariable,
          Rows:    [...(baseVariable.Rows    || []), ...newRows],
          varName: [...(baseVariable.varName || []), ...data.varNames],
        };
        setSelectedVariable(updatedVariable);
        const updatedDataGroup = { ...dataGroup, [selectedKey]: updatedVariable };
        setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      } else {
        // Keep the original variable (without customRows), add a new Multiple2DGrid variable
        const newKey = selectedKey + "new";
        const updatedOriginal = { ...baseVariable };
        const newVariable = {
          ...baseVariable,
          type: "Multiple2DGrid",
          Rows: newRows,
          values: newValues,
          valueLabels: newValueLabels,
          varName: data.varNames,
        };
        setSelectedVariable(updatedOriginal);
        const updatedDataGroup = {
          ...dataGroup,
          [selectedKey]: updatedOriginal,
          [newKey]: newVariable,
        };
        setdataGroup((prev) => ({
          ...prev,
          [selectedKey]: updatedOriginal,
          [newKey]: newVariable,
        }));
        await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      }

      toast.success(`Variable saved! ${data.varNames.length} binary variables created.`);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} row(s) had errors and were skipped.`);
      }
    } catch (error) {
      toast.error("Error saving variable: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Commit a conversion patch ({ remove, add }) from ConvertTypeModal / Duplicate
  // to the dataGroup and persist it. Keeps a snapshot for one-step undo.
  const applyConversionPatch = async (patch, label) => {
    setConvSnapshot({ dataGroup: { ...dataGroup }, key: selectedKey });
    const updated = { ...dataGroup };
    (patch.remove || []).forEach((k) => delete updated[k]);
    Object.assign(updated, patch.add);
    setdataGroup(updated);
    if ((patch.remove || []).includes(selectedKey)) {
      setSelectedVariable(null);
    } else if (patch.add[selectedKey]) {
      setSelectedVariable(patch.add[selectedKey]);
      setActiveTab(SPECIAL_ROWS[patch.add[selectedKey].type] ? "specialRows" : "properties");
    }
    setLoading(true);
    try {
      await apiService.post("/save_all_data_list", { projectId, dataGroup: updated });
      toast.success(`Applied: ${label}`);
    } catch (error) {
      toast.error("Error saving conversion: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const undoConversion = async () => {
    if (!convSnapshot) return;
    setLoading(true);
    try {
      setdataGroup(convSnapshot.dataGroup);
      setSelectedVariable(convSnapshot.dataGroup[convSnapshot.key] || null);
      await apiService.post("/save_all_data_list", { projectId, dataGroup: convSnapshot.dataGroup });
      setConvSnapshot(null);
      toast.success("Conversion undone.");
    } catch (error) {
      toast.error("Error undoing conversion: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedVariable) return;
    const patch = duplicateVariable(selectedVariable, selectedKey, dataGroup);
    await applyConversionPatch(patch, `duplicated as ${patch.newKey}`);
  };

  // Single2DGrid → Custom (OE Combo): one custom row per value, with logic
  // OR-ing that value across every grid varName — e.g. Var1=1 or Var2=1 or Var3=1.
  // Values are removed (stashed) once converted.
  const handleOECombo = async () => {
    if (!selectedVariable || selectedVariable.type !== 'Single2DGrid') return;

    const values = selectedVariable.values || [];
    const varNames = selectedVariable.varName || [];

    if (values.length === 0) {
      toast.warning("No values defined to convert.");
      return;
    }
    if (varNames.length === 0) {
      toast.warning("No varNames defined to build logic from.");
      return;
    }

    const confirmed = await confirm({
      title: "Convert to Custom (OE Combo)",
      message: `This will convert "${selectedKey}" to Custom type, creating ${values.length} custom row(s) — one per value — with logic OR-ing that value across all ${varNames.length} grid variable(s). Values will be removed. Proceed?`,
      confirmText: "Convert",
    });
    if (!confirmed) return;

    const newCustomRows = values.map((val) => ({
      index: Number(val.index),
      label: val.title || String(val.index),
      logic: varNames.map((vn) => `${vn.Name}=${val.index}`).join(" or "),
      logicBase: "",
      norank: false,
      fix: false,
    }));

    const { values: _removedValues, valueLabels: _removedValueLabels, ...rest } = selectedVariable;
    const updatedVariable = {
      ...rest,
      type: "Custom",
      customRows: [...(selectedVariable.customRows || []), ...newCustomRows],
      _savedValues: values,
      _savedValueLabels: selectedVariable.valueLabels || {},
    };

    const patch = { remove: [], add: { [selectedKey]: updatedVariable } };
    await applyConversionPatch(patch, `${selectedKey} converted to Custom (OE Combo)`);
  };

  const handleSwapRowsAndCols = async () => {
    if (!selectedVariable?.Rows || !selectedVariable?.Cols) {
      toast.warning("Cannot swap: Missing rows or columns data");
      return;
    }

    const isGridType = selectedVariable.type && 
      (selectedVariable.type.includes("2DGrid") || 
       selectedVariable.type.includes("3DGrid") ||
       selectedVariable.type.includes("Grid"));

    if (!isGridType) {
      toast.warning("Cannot swap: Only grid-type variables support row/column swapping");
      return;
    }

    const confirmSwap = await confirm({
      title: "Swap Rows and Columns",
      message: `Are you sure you want to swap ${selectedVariable.Rows.length} rows with ${selectedVariable.Cols.length} columns?`,
      confirmText: "Swap",
    });
    
    if (!confirmSwap) return;

    const oldRows = JSON.parse(JSON.stringify(selectedVariable.Rows || []));
    const oldCols = JSON.parse(JSON.stringify(selectedVariable.Cols || []));

    const newRows = oldCols.map(col => ({
      index: col.index,
      title: col.title,
      exclusive: col.exclusive || false,
      ...Object.fromEntries(
        Object.entries(col).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    const newCols = oldRows.map(row => ({
      index: row.index,
      title: row.title,
      ...Object.fromEntries(
        Object.entries(row).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    setSelectedVariable(prev => ({
      ...prev,
      Rows: newRows,
      Cols: newCols,
      ...(prev.rowCount && { rowCount: prev.colCount }),
      ...(prev.colCount && { colCount: prev.rowCount })
    }));

    toast.success(`Swapped: ${oldRows.length} rows ↔ ${oldCols.length} cols → now ${newRows.length} rows, ${newCols.length} cols`);
  };

  const createNet = (label, rowIndices) => {
    const newNet = { label, indices: rowIndices };
    setNets((prevNets) => {
      const updatedNets = [...prevNets, newNet];
      addNet(updatedNets);
      return updatedNets;
    });
  };

  const addNet = (updatedNets) => {
    setSelectedVariable((prev) => ({ ...prev, Nets: updatedNets }));
  };

  const saveChanges = async () => {
    if (!selectedVariable) return;

    setLoading(true);
    
    try {
      const updatedDataGroup = { ...dataGroup, [selectedKey]: selectedVariable };
      setdataGroup((prev) => ({ ...prev, [selectedKey]: selectedVariable }));
      setNets([]);

      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      toast.success("Data saved successfully");
    } catch (error) {
      toast.error("Error saving changes: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowsChange = (updatedRows) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Rows: updatedRows }));
    }
  };

  const handleColsChange = (updatedCols) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Cols: updatedCols }));
    }
  };

  const handlestatementsChange = (updatedStatements) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Statements: updatedStatements }));
    }
  };

  const handlevaluesChange = (updatedValues) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, values: updatedValues }));
    }
  };

  const handlevalueLabelsChange = (updatedvalueLabels) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, valueLabels: updatedvalueLabels }));
    }
  };

  const handleNetPropertyChange = (e, property) => {
    if (!selectedVariable) return;

    const newValue = e.target.value;
    
    const processNetValue = (value) => {
      if (!value || typeof value !== 'string') return [];
      
      const netDefinitions = value.split(';').map(part => part.trim()).filter(part => part !== '');
      const nets = [];
      
      netDefinitions.forEach(definition => {
        const parts = definition.split('=').map(part => part.trim());
        
        if (parts.length >= 2) {
          const label = parts[0];
          const indicesPart = parts[1];
          const indices = processIndicesString(indicesPart);
          nets.push({ label, indices });
        } else if (parts.length === 1) {
          const indices = processIndicesString(parts[0]);
          nets.push({ label: parts[0], indices: indices.length > 0 ? indices : [] });
        }
      });
      
      return nets;
    };

    const processIndicesString = (str) => {
      if (!str || typeof str !== 'string') return [];
      
      const result = [];
      const parts = str.split(',').map(part => part.trim());
      
      parts.forEach(part => {
        if (part.includes('-')) {
          const rangeParts = part.split('-').map(p => p.trim());
          if (rangeParts.length === 2) {
            const start = parseInt(rangeParts[0], 10);
            const end = parseInt(rangeParts[1], 10);
            if (!isNaN(start) && !isNaN(end)) {
              for (let i = start; i <= end; i++) result.push(i);
            }
          }
        } else {
          const num = parseInt(part, 10);
          if (!isNaN(num)) result.push(num);
        }
      });
      
      return [...new Set(result)].sort((a, b) => a - b);
    };

    const processedNets = processNetValue(newValue);
    setSelectedVariable((prev) => ({ ...prev, [property]: newValue, Nets: processedNets }));
  };

  const handlePropertyChange = (e, property) => {
    if (!selectedVariable) return;
    const newValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSelectedVariable((prev) => {
      const next = { ...prev, [property]: newValue };
      const alias = PROPERTY_ALIASES[property];
      if (alias) delete next[alias];
      return next;
    });
  };

  const renderTabContent = () => {
    if (!selectedVariable) return <div>No variable data available</div>;

    switch (activeTab) {
      case "properties":
        return (
          <div className="properties-container">

            {/* ── Question Title banner ── */}
            {selectedVariable?.Qtitle && (
              <div className="prop-qtitle-banner">
                <span className="prop-qtitle-label">Q</span>
                <span className="prop-qtitle-text">{selectedVariable.Qtitle}</span>
              </div>
            )}

            {/* ── Boolean toggles ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">⚙</span>
                <span className="prop-section-title">Settings</span>
              </div>
              <div className="prop-toggle-grid">
                {Object.entries(allBooleanProperties).map(([property, defaultValue]) => {
                  const isOn = getPropertyValue(property, defaultValue);
                  return (
                    <label
                      key={property}
                      className={`prop-toggle-card ${isOn ? "prop-toggle-card--on" : ""}`}
                      title={property}
                    >
                      <span className="prop-toggle-name">{TOGGLE_LABELS[property] || property}</span>
                      <div className={`prop-toggle-track ${isOn ? "prop-toggle-track--on" : ""}`}>
                        <div className="prop-toggle-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handlePropertyChange(e, property)}
                        style={{ display: "none" }}
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* ── Stats Rows (Multiple2DGrid / Multiple3DGrid only, when mean or allstats is on) ── */}
            {(selectedVariable.type === 'Multiple2DGrid' || selectedVariable.type === 'Multiple3DGrid' || selectedVariable.type === 'Custom') &&
              (getPropertyValue('mean', false) || getPropertyValue('allstats', false)) && (
              <div className="prop-section">
                <div className="prop-section-header">
                  <span className="prop-section-icon">∑</span>
                  <span className="prop-section-title">Stat Rows</span>
                </div>
                <div className="prop-text-grid">
                  <div className="prop-text-field prop-text-field--wide">
                    <label className="prop-text-label">Row indices for stats (e.g. 1-7 or 1,3,4)</label>
                    <input
                      type="text"
                      className="prop-text-input"
                      value={getPropertyValue('statsRows', (() => {
                        const idxs = (selectedVariable.Rows || [])
                          .map(r => Number(r.index)).filter(i => !isNaN(i) && i > 0);
                        return idxs.length ? `${Math.min(...idxs)}-${Math.max(...idxs)}` : '';
                      })())}
                      placeholder="e.g. 1-7 or 1,3,4"
                      onChange={(e) => handlePropertyChange(e, 'statsRows')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Ranking ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">↕</span>
                <span className="prop-section-title">Ranking</span>
              </div>
              <div className="prop-toggle-grid">
                {[
                  { key: 'rank',             label: 'Rank' },
                  { key: 'rankalphabetical', label: 'Alphabetical' },
                  { key: 'netswithingroups', label: 'Nets in Groups' },
                ].map(({ key, label }) => {
                  const isOn = getPropertyValue(key, false);
                  return (
                    <label
                      key={key}
                      className={`prop-toggle-card ${isOn ? "prop-toggle-card--on" : ""}`}
                      title={key}
                    >
                      <span className="prop-toggle-name">{label}</span>
                      <div className={`prop-toggle-track ${isOn ? "prop-toggle-track--on" : ""}`}>
                        <div className="prop-toggle-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handlePropertyChange(e, key)}
                        style={{ display: "none" }}
                      />
                    </label>
                  );
                })}
              </div>
              {getPropertyValue('rank', false) && !getPropertyValue('rankalphabetical', false) && (
                <div className="prop-rank-config">
                  <div className="prop-text-field">
                    <label className="prop-text-label">Rank Col (0 = Total)</label>
                    <input
                      type="number"
                      min="0"
                      className="prop-text-input"
                      value={getPropertyValue('rankcol', 0)}
                      onChange={(e) => setPropertyValue('rankcol', parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                  <div className="prop-rank-radio">
                    <label className="prop-rank-radio-option">
                      <input
                        type="radio"
                        name={`rankby-${selectedKey}`}
                        checked={!getPropertyValue('rankbyfreq', false)}
                        onChange={() => setPropertyValue('rankbyfreq', false)}
                      />
                      <span>% (Percentage)</span>
                    </label>
                    <label className="prop-rank-radio-option">
                      <input
                        type="radio"
                        name={`rankby-${selectedKey}`}
                        checked={getPropertyValue('rankbyfreq', false)}
                        onChange={() => setPropertyValue('rankbyfreq', true)}
                      />
                      <span>Freq (Count)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* ── Text fields ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">✎</span>
                <span className="prop-section-title">Configuration</span>
              </div>
              <div className="prop-text-grid">
                {[
                  { key: "type",         label: "Type",           placeholder: "" },
                  { key: "table",        label: "Table",          placeholder: "freq" },
                  { key: "Qtitle",       label: "Q Title",        placeholder: "Question title..." },
                  { key: "tabSpectitle", label: "Tab Spec Title", placeholder: "Title shown in the table..." },
                  { key: "basetitle",    label: "Base Title",     placeholder: "All respondents" },
                  { key: "Logic",        label: "Logic",          placeholder: "" },
                  { key: "combo",        label: "Combo",          placeholder: "S3 or S4, S3 and S4;S3 and S4" },
                  { key: "flttb",        label: "Flttb",          placeholder: "S3, S4=1-4 etc" },
                  { key: "stacked",      label: "Stacked",        placeholder: "1,3,4;1,6,9" },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} className="prop-text-field">
                    <label className="prop-text-label">{label}</label>
                    <input
                      type="text"
                      className="prop-text-input"
                      value={getPropertyValue(key, "")}
                      placeholder={placeholder}
                      onChange={(e) => handlePropertyChange(e, key)}
                    />
                  </div>
                ))}

                <div className="prop-text-field">
                  <label className="prop-text-label">Min</label>
                  <input
                    type="number"
                    className="prop-text-input"
                    value={getPropertyValue("min", "")}
                    placeholder="0"
                    onChange={(e) => handlePropertyChange(e, "min")}
                  />
                </div>
                <div className="prop-text-field">
                  <label className="prop-text-label">Max</label>
                  <input
                    type="number"
                    className="prop-text-input"
                    value={getPropertyValue("max", "")}
                    placeholder="99999999"
                    onChange={(e) => handlePropertyChange(e, "max")}
                  />
                </div>

                {/* nets uses special handler */}
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Nets (text definition)</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("nets", "")}
                    placeholder="NET1=1-5;NET2=6-10;11-15"
                    onChange={(e) => handleNetPropertyChange(e, "nets")}
                  />
                </div>
                                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">New Key</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("newkey", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handlePropertyChange(e, "newkey")}
                  />
                </div>
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Weight Variable</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("weightvar", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handlePropertyChange(e, "weightvar")}
                  />
                </div>                
              </div>
            </div>
          </div>
        );

      case "rows": {
        const isGridRankType = selectedVariable.type === 'Multiple2DGrid' || selectedVariable.type === 'Multiple3DGrid';
        return selectedVariable.Rows?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Rows}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handleRowsChange}
            typeofproperty={"rows"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
            showRowFixNoRank={isGridRankType}
          />
        ) : (
          <div className="no-data-message">No rows data available</div>
        );
      }

      case "cols":
        return selectedVariable.Cols?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Cols}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handleColsChange}
            typeofproperty={"cols"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
          />
        ) : (
          <div className="no-data-message">No columns data available</div>
        );

      case "values":
        return selectedVariable.values?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.values}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handlevaluesChange}
            typeofproperty={"values"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
          />
        ) : (
          <div className="no-data-message">No values data available</div>
        );

      case "statements":
        return selectedVariable.Statements?.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={selectedVariable.Statements}
            onClose={() => setActiveTab("properties")}
            onRowsChange={handlestatementsChange}
            typeofproperty={"statements"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
          />
        ) : (
          <div className="no-data-message">No statements data available</div>
        );

      case "valueLabels": {
        const valueLabelsArray = selectedVariable.valueLabels
          ? Object.entries(selectedVariable.valueLabels).map(([key, value]) => ({ index: key, title: value }))
          : [];
        return valueLabelsArray.length > 0 ? (
          <TableSettingModal
            isOpen={true}
            rows={valueLabelsArray}
            onClose={() => setActiveTab("properties")}
            onRowsChange={(updatedRows) => {
              const updatedValueLabels = {};
              updatedRows.forEach((row) => { updatedValueLabels[row.index] = row.title; });
              handlevalueLabelsChange(updatedValueLabels);
            }}
            typeofproperty={"valueLabels"}
            createNet={createNet}
            netLabel={netLabel}
            setNetLabel={setNetLabel}
          />
        ) : (
          <div className="no-data-message">No value labels data available</div>
        );
      }

      case "customRows": {
        const customRowsList = selectedVariable?.customRows || [];

        const getMaxIndex = () => {
          const type = selectedVariable?.type || "";
          const isMultipleType = type.includes("Multiple") || type.includes("MultipleGrid");
          if (isMultipleType && selectedVariable?.Rows?.length > 0) {
            return Math.max(...selectedVariable.Rows.map(r => parseInt(r.index) || 0), 0);
          }
          if (selectedVariable?.values?.length > 0) {
            return Math.max(...selectedVariable.values.map(v => parseInt(v.index) || 0), 0);
          }
          return 0;
        };

        const addCustomRow = () => {
          const base = getMaxIndex();
          const nextIndex = base + customRowsList.length + 1;
          const updated = [...customRowsList, { index: nextIndex, label: "", logic: "", logicBase: "", norank: false, fix: false }];
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const updateCustomRow = (idx, field, value) => {
          const updated = customRowsList.map((r, i) => i === idx ? { ...r, [field]: value } : r);
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const deleteCustomRow = (idx) => {
          const updated = customRowsList.filter((_, i) => i !== idx);
          setSelectedVariable(prev => ({ ...prev, customRows: updated }));
        };

        const handlePasteFromExcel = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split('\n').filter(l => l.trim());
          const base = getMaxIndex();
          const parsed = [];
          lines.forEach((line, i) => {
            const parts = line.split('\t');
            let idx, label, logic, logicBase;
            if (parts.length === 1) {
              idx = base + customRowsList.length + i + 1;
              label = parts[0].trim();
              logic = ""; logicBase = "";
            } else if (parts.length === 2) {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = ""; logicBase = "";
            } else if (parts.length === 3) {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = parts[2].trim(); logicBase = "";
            } else {
              idx = parseInt(parts[0].trim()) || (base + customRowsList.length + i + 1);
              label = parts[1].trim();
              logic = parts[2].trim();
              logicBase = parts[3].trim();
            }
            if (label) parsed.push({ index: idx, label, logic, logicBase, norank: false, fix: false });
          });
          if (parsed.length > 0) {
            const updated = [...customRowsList, ...parsed];
            setSelectedVariable(prev => ({ ...prev, customRows: updated }));
            e.target.value = '';
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {customRowsList.length} Custom Row{customRowsList.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addCustomRow}>+ Add Row</button>
            </div>

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: index | label | logic | logicBase — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={"101\tCustom Row A\tQ1=1 AND Q2=2\tQ1=1\n102\tCustom Row B\tQ3=3\t"}
                onChange={handlePasteFromExcel}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-index">Index</th>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Logic</th>
                  <th className="cr-th cr-th-logic">Logic Base</th>
                  <th className="cr-th" style={{width:'6%'}}>No Rank</th>
                  <th className="cr-th" style={{width:'5%'}}>Fix</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {customRowsList.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="number"
                        className="cr-input cr-input-index"
                        value={row.index}
                        onChange={e => updateCustomRow(idx, 'index', parseInt(e.target.value) || 0)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={e => updateCustomRow(idx, 'label', e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.logic}
                        placeholder="Q1=1 AND Q2=2"
                        onChange={e => updateCustomRow(idx, 'logic', e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.logicBase || ""}
                        placeholder="Base filter (optional)"
                        title="Respondents matching this expression become the % denominator"
                        onChange={e => updateCustomRow(idx, 'logicBase', e.target.value)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Exclude from ranking (keep at bottom)"
                        checked={!!row.norank}
                        onChange={e => updateCustomRow(idx, 'norank', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td" style={{textAlign:'center'}}>
                      <input
                        type="checkbox"
                        title="Keep this row at its original position during ranking"
                        checked={!!row.fix}
                        onChange={e => updateCustomRow(idx, 'fix', e.target.checked)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteCustomRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {customRowsList.length === 0 && (
                  <tr>
                    <td colSpan="7" className="cr-empty">
                      No custom rows. Click "+ Add Row" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      }

      case "varNames": {
        const varNameList = selectedVariable?.varName || [];
        const setVarNameList = (updated) => setSelectedVariable((prev) => ({ ...prev, varName: updated }));

        const addVarNameRow = () =>
          setVarNameList([...varNameList, { Name: "", row: "", col: "", stat: "", Location: "" }]);

        const updateVarNameRow = (i, field, value) =>
          setVarNameList(varNameList.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteVarNameRow = (i) => setVarNameList(varNameList.filter((_, j) => j !== i));

        const handleVarNamePaste = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split("\n").filter((l) => l.trim());
          const parsed = [];
          lines.forEach((line) => {
            const parts = line.split("\t");
            const [name = "", row = "", col = "", stat = "", location = ""] = parts.map((p) => p.trim());
            if (name) parsed.push({ Name: name, row, col, stat, Location: location });
          });
          if (parsed.length > 0) {
            setVarNameList([...varNameList, ...parsed]);
            e.target.value = "";
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {varNameList.length} VarName{varNameList.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addVarNameRow}>+ Add VarName</button>
            </div>

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: name | row | col | stat | location — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={"Grid_Q1_1\t1\t\t\t10:11\nGrid_Q1_2\t2\t\t\t12:13"}
                onChange={handleVarNamePaste}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-label">Name</th>
                  <th className="cr-th" style={{ width: "12%" }}>Row</th>
                  <th className="cr-th" style={{ width: "12%" }}>Col</th>
                  <th className="cr-th" style={{ width: "12%" }}>Stat</th>
                  <th className="cr-th cr-th-logic">Location</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {varNameList.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.Name ?? ""}
                        placeholder="Q1_1"
                        onChange={(e) => updateVarNameRow(idx, "Name", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.row ?? ""}
                        placeholder="Row idx"
                        onChange={(e) => updateVarNameRow(idx, "row", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.col ?? ""}
                        placeholder="Col idx"
                        onChange={(e) => updateVarNameRow(idx, "col", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-index"
                        value={row.stat ?? ""}
                        placeholder="Stat"
                        onChange={(e) => updateVarNameRow(idx, "stat", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.Location ?? ""}
                        placeholder="10:11"
                        onChange={(e) => updateVarNameRow(idx, "Location", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteVarNameRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {varNameList.length === 0 && (
                  <tr>
                    <td colSpan="6" className="cr-empty">
                      No varNames yet. Click "+ Add VarName" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      }

      case "nets": {
        const parseIndices = (str) => {
          if (!str || typeof str !== "string") return [];
          const result = [];
          str.split(",").map(p => p.trim()).forEach(part => {
            if (part.includes("-")) {
              const [s, e] = part.split("-").map(p => parseInt(p.trim(), 10));
              if (!isNaN(s) && !isNaN(e)) for (let i = s; i <= e; i++) result.push(i);
            } else {
              const n = parseInt(part, 10);
              if (!isNaN(n)) result.push(n);
            }
          });
          return [...new Set(result)].sort((a, b) => a - b);
        };

        const showWithin = getPropertyValue("showNetswithinLabels", false);

        return selectedVariable?.Nets?.length > 0 ? (
          <div className="nets-container">
            <div className="nets-header-row">
              <span className="nets-count-badge">{selectedVariable.Nets.length} Net{selectedVariable.Nets.length !== 1 ? "s" : ""}</span>
              <label className="nets-toggle-wrap" title="Show Nets within Labels">
                <span className="nets-toggle-label">Show within labels</span>
                <div
                  className={`prop-toggle-track ${showWithin ? "prop-toggle-track--on" : ""}`}
                  onClick={() => setPropertyValue("showNetswithinLabels", !showWithin)}
                >
                  <div className="prop-toggle-thumb" />
                </div>
              </label>
            </div>
            <div className="nets-list">
              {selectedVariable.Nets.map((net, netIdx) => {
                const isNps = String(net.label ?? '').toLowerCase() === "nps";
                const hasNoIndices = net.indices === null || net.indices === undefined;
                // Comparison-only expression stored in indicesRaw (e.g. ">100", "<50, >200")
                const isCmpExpr = hasNoIndices && !isNps && net.indicesRaw && /[<>]/.test(net.indicesRaw);
                const _safeIndices = Array.isArray(net.indices) ? net.indices : [];
                const displayIndices = (!isCmpExpr && (isNps || hasNoIndices)) ? [...Array(11).keys()] : (_safeIndices.length ? _safeIndices : []);

                // indicesRaw always wins for the input display — it preserves partial
                // input like "55-" while the user is still typing.
                const indicesText = net.indicesRaw !== undefined
                  ? net.indicesRaw
                  : (isNps || hasNoIndices ? "" : _safeIndices.join(", "));

                return (
                  <div key={netIdx} className="net-card">
                    <div className="net-card-header">
                      <div className="net-card-index">#{netIdx + 1}</div>
                      <div className="net-card-label-wrap">
                        <input
                          className="net-card-label-input"
                          value={net.label}
                          placeholder="Net label..."
                          onChange={(e) => {
                            const updated = selectedVariable.Nets.map((n, i) =>
                              i === netIdx ? { ...n, label: e.target.value } : n
                            );
                            setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                          }}
                        />
                      </div>
                      <button
                        className="net-card-delete"
                        title="Remove this net"
                        onClick={() => {
                          const updated = selectedVariable.Nets.filter((_, i) => i !== netIdx);
                          setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                          setNets(updated);
                        }}
                      >✕</button>
                    </div>
                    <div className="net-card-indices-edit">
                      <label className="net-indices-label">Indices</label>
                      <input
                        className="net-indices-input"
                        value={indicesText}
                        placeholder="e.g. 1, 2, 55-99"
                        onChange={(e) => {
                          const raw = e.target.value;
                          const parsed = parseIndices(raw);
                          // Pure comparison expression like ">100" or "<50, >200" —
                          // no numeric indices to expand here; backend resolves via indicesRaw.
                          const isCmp = parsed.length === 0 && raw.trim() !== "" && /[<>]/.test(raw);
                          const updated = selectedVariable.Nets.map((n, i) =>
                            i === netIdx
                              ? {
                                  ...n,
                                  indicesRaw: raw,
                                  indices: raw.trim() === "" ? null
                                         : isCmp           ? null   // backend uses indicesRaw
                                         : parsed.length > 0 ? parsed
                                         : n.indices,
                                }
                              : n
                          );
                          setSelectedVariable(prev => ({ ...prev, Nets: updated }));
                        }}
                      />
                    </div>
                    <div className="net-card-indices">
                      {isCmpExpr ? (
                        <span className="net-index-chip net-index-chip--cmp" title="Resolved at calculation time against actual data">
                          <span className="net-index-num">{net.indicesRaw}</span>
                        </span>
                      ) : displayIndices.length > 20 ? (
                        <span className="net-index-chip net-index-chip--range">
                          <span className="net-index-num">{displayIndices[0]}–{displayIndices[displayIndices.length - 1]}</span>
                          <span className="net-indices-count">({displayIndices.length} values)</span>
                        </span>
                      ) : (
                        displayIndices.map((idx, iIdx) => (
                          <span key={iIdx} className="net-index-chip">
                            <span className="net-index-num">{idx}</span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="no-data-message">No nets defined for this variable</div>
        );
      }

      case "specialRows": {
        // Stacked / Volume / Share row editor — same row shape for all three:
        // { index, label, varNames: [], divisionLogic? (Share only) }
        const cfg = SPECIAL_ROWS[selectedVariable?.type]
          || Object.values(SPECIAL_ROWS).find((c) => (selectedVariable?.[c.prop] || []).length);
        if (!cfg) return <div className="no-data-message">No special rows for this type</div>;

        const list = selectedVariable[cfg.prop] || [];
        const setList = (updated) => setSelectedVariable((prev) => ({ ...prev, [cfg.prop]: updated }));

        const addRow = () =>
          setList([...list, { index: list.length + 1, label: "", varNames: [], ...(cfg.division ? { divisionLogic: "" } : {}) }]);

        const updateRow = (i, field, value) =>
          setList(list.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteRow = (i) => setList(list.filter((_, j) => j !== i));

        const parseVars = (text) =>
          String(text || "").split(/[,;]+/).map((s) => s.trim()).filter(Boolean);

        const handleSpecialPaste = (e) => {
          const text = e.target.value;
          if (!text.trim()) return;
          const lines = text.trim().split("\n").filter((l) => l.trim());
          const parsed = [];
          lines.forEach((line, i) => {
            const parts = line.split("\t");
            let idx, label, vars, division;
            if (parts.length === 1) {
              idx = list.length + parsed.length + 1; label = parts[0].trim(); vars = []; division = "";
            } else if (parts.length === 2) {
              idx = list.length + parsed.length + 1; label = parts[0].trim(); vars = parseVars(parts[1]); division = "";
            } else {
              idx = parseInt(parts[0].trim()) || (list.length + parsed.length + 1);
              label = parts[1].trim();
              vars = parseVars(parts[2]);
              division = (parts[3] || "").trim();
            }
            if (label) {
              const row = { index: idx, label, varNames: vars };
              if (cfg.division) row.divisionLogic = division;
              parsed.push(row);
            }
          });
          if (parsed.length > 0) {
            setList([...list, ...parsed]);
            e.target.value = "";
          }
        };

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {list.length} {cfg.label.replace(" Rows", "")} Row{list.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addRow}>+ Add Row</button>
            </div>

            {cfg.division && (
              <div className="prop-text-field prop-text-field--wide" style={{ marginBottom: 10 }}>
                <label className="prop-text-label">
                  Default Division (used when a row has no division of its own — logic counts respondents, variable names are summed)
                </label>
                <input
                  type="text"
                  className="prop-text-input"
                  value={getPropertyValue("defaultDivision", "")}
                  placeholder="e.g. S1=1  or  Q7r1, Q7r2"
                  onChange={(e) => handlePropertyChange(e, "defaultDivision")}
                />
              </div>
            )}

            <div className="cr-paste-area">
              <label className="cr-paste-label">
                Paste from Excel (tab-separated: index | label | variables{cfg.division ? " | division" : ""} — one row per line)
              </label>
              <textarea
                className="cr-paste-textarea"
                placeholder={`1\tBrand A\tQ7r1, Q7r2${cfg.division ? "\tS1=1" : ""}\n2\tBrand B\tQ8r1${cfg.division ? "\t" : ""}`}
                onChange={handleSpecialPaste}
              />
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-index">Index</th>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Variables (comma-separated)</th>
                  {cfg.division && <th className="cr-th cr-th-logic">Division</th>}
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="number"
                        className="cr-input cr-input-index"
                        value={row.index}
                        onChange={(e) => updateRow(idx, "index", parseInt(e.target.value) || 0)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={(e) => updateRow(idx, "label", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.varNamesText !== undefined ? row.varNamesText : (row.varNames || []).join(", ")}
                        placeholder="Q7r1, Q7r2"
                        onChange={(e) =>
                          setList(list.map((r, j) =>
                            j === idx
                              ? { ...r, varNamesText: e.target.value, varNames: parseVars(e.target.value) }
                              : r
                          ))
                        }
                      />
                    </td>
                    {cfg.division && (
                      <td className="cr-td">
                        <input
                          type="text"
                          className="cr-input cr-input-logic"
                          value={row.divisionLogic || ""}
                          placeholder="Logic (count) or variables (sum)"
                          title="A logic expression counts matching respondents; variable names are summed. Empty = use Default Division."
                          onChange={(e) => updateRow(idx, "divisionLogic", e.target.value)}
                        />
                      </td>
                    )}
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={cfg.division ? 5 : 4} className="cr-empty">
                      No rows yet. Click "+ Add Row" or paste from Excel above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      }

      case "rowCalcs": {
        const list = selectedVariable?.RowCalcs || [];
        const setList = (updated) => setSelectedVariable((prev) => ({ ...prev, RowCalcs: updated }));

        const addRow = () => setList([...list, { label: "", expr: "" }]);

        const updateRow = (i, field, value) =>
          setList(list.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

        const deleteRow = (i) => setList(list.filter((_, j) => j !== i));

        return (
          <div className="cr-container">
            <div className="cr-header">
              <span className="cr-count-badge">
                {list.length} Row Calc{list.length !== 1 ? "s" : ""}
              </span>
              <button className="cr-add-btn" onClick={addRow}>+ Add Row Calc</button>
            </div>

            <table className="cr-table">
              <thead>
                <tr>
                  <th className="cr-th cr-th-label">Label</th>
                  <th className="cr-th cr-th-logic">Calculation</th>
                  <th className="cr-th cr-th-del"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={idx} className="cr-row">
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-label"
                        value={row.label}
                        placeholder="Row label..."
                        onChange={(e) => updateRow(idx, "label", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <input
                        type="text"
                        className="cr-input cr-input-logic"
                        value={row.expr}
                        placeholder="e.g. R1+R2+R3, R1*R4, R1/R2, P1+P4, R-5556"
                        onChange={(e) => updateRow(idx, "expr", e.target.value)}
                      />
                    </td>
                    <td className="cr-td">
                      <button className="cr-del-btn" title="Remove" onClick={() => deleteRow(idx)}>✕</button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan="3" className="cr-empty">
                      No row calculations yet. Click "+ Add Row Calc".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="no-data-message" style={{ marginTop: 8 }}>
              Reference any row by its index number: <code>R&lt;n&gt;</code> pulls that row's frequency (e.g. R1, R4, or
              R-5556 for a Net row), <code>P&lt;n&gt;</code> pulls that row's own percentage instead. Combine with
              + − × ÷ and parentheses — e.g. R1+R2+R3, R1*R4, R1/R2, P1+P4, (R1+R2)/R3. Mixing R and P in one
              expression is allowed but the result is treated as a percentage (no raw count) as soon as any P appears.
            </div>
          </div>
        );
      }

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  const handleRefreshFromSpss = async () => {
    if (!selectedVariable || !selectedKey) return;

    const varNames = selectedVariable.varName;
    if (!varNames || varNames.length === 0) {
      toast.warning("No varNames defined for this variable — cannot refresh from SPSS.");
      return;
    }

    const confirmed = await confirm({
      title: "Refresh Data from SPSS",
      message: `This will reload data for "${selectedKey}" (${varNames.length} variable(s)) from the SPSS file and overwrite the current values in the database. This cannot be undone. Proceed?`,
      confirmText: "Refresh",
    });
    if (!confirmed) return;

    setRefreshLoading(true);
    try {
      const { data } = await apiService.post('/refresh-variable-from-spss', {
        projectId,
        varNames,
        user: powner || '',
      });

      if (!data.success) {
        toast.error("Refresh failed: " + (data.error || "Unknown error"));
        return;
      }

      toast.success(data.message || `Refreshed ${data.updated?.length || 0} variable(s) from SPSS`);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} variable(s) had issues: ${data.errors.join("; ")}`);
      }
    } catch (error) {
      toast.error("Error refreshing from SPSS: " + error.message);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleSwitchBetween = (from, to) => {
    const fromItems = selectedVariable[from] || [];
    const toItems = selectedVariable[to] || [];
    const updatedVariable = { ...selectedVariable, [from]: toItems, [to]: fromItems };
    setSelectedVariable(updatedVariable);
    setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
  };

  const hasRows = selectedVariable?.Rows?.length > 0;
  const specialCfg = selectedVariable
    ? (SPECIAL_ROWS[selectedVariable.type]
       || Object.values(SPECIAL_ROWS).find((c) => (selectedVariable[c.prop] || []).length > 0))
    : null;
  const hasCols = selectedVariable?.Cols?.length > 0;
  const hasStatements = selectedVariable?.Statements?.length > 0;
  const hasValues = selectedVariable?.values?.length > 0;
  const hasValueLabels = selectedVariable?.valueLabels && Object.keys(selectedVariable.valueLabels).length > 0;
  const hasNets = selectedVariable?.Nets?.length > 0;

  return (
    <div className="variable-properties">
      <h2>{selectedKey || "No variable selected"}</h2>

      <div className="tab-navigation">
        <CommonButton
          title="Properties Tab"
          label="Properties"
          onClick={() => setActiveTab("properties")}
          variant="tab"
          isSelected={activeTab === "properties"}
          size="small"
          bold={true}
        />

        {hasRows && (
          <CommonButton
            title={`Rows Tab (${selectedVariable.Rows.length})`}
            label={`Rows (${selectedVariable.Rows.length})`}
            onClick={() => setActiveTab("rows")}
            variant="tab"
            isSelected={activeTab === "rows"}
            size="small"
            bold={true}
          />
        )}

        {hasCols && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Rows"
              onClick={() => handleSwitchBetween("Cols", "Rows")}
              variant="arrow"
            />
            <CommonButton
              title={`Columns Tab (${selectedVariable.Cols.length})`}
              label={`Columns (${selectedVariable.Cols.length})`}
              onClick={() => setActiveTab("cols")}
              variant="tab"
              isSelected={activeTab === "cols"}
              size="small"
              bold={true}
            />
          </>
        )}

        {hasStatements && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Statements"
              onClick={() => handleSwitchBetween("Cols", "Statements")}
              variant="arrow"
            />
            <CommonButton
              title={`Statements Tab (${selectedVariable.Statements.length})`}
              label={`Statements (${selectedVariable.Statements.length})`}
              onClick={() => setActiveTab("statements")}
              variant="tab"
              isSelected={activeTab === "statements"}
              size="small"
              bold={true}
            />
            <CommonButton
              label="⤴"
              title="Switch Rows ↔ Statements"
              onClick={() => handleSwitchBetween("Rows", "Statements")}
              variant="arrow"
            />
          </>
        )}

        {hasValues && (
          <CommonButton
            title={`Values Tab (${selectedVariable.values.length})`}
            label={`Values (${selectedVariable.values.length})`}
            onClick={() => setActiveTab("values")}
            variant="tab"
            isSelected={activeTab === "values"}
            size="small"
            bold={true}
          />
        )}

        {hasValueLabels && (
          <CommonButton
            title={`Value Labels Tab (${Object.keys(selectedVariable.valueLabels).length})`}
            label={`Value Labels (${Object.keys(selectedVariable.valueLabels).length})`}
            onClick={() => setActiveTab("valueLabels")}
            variant="tab"
            isSelected={activeTab === "valueLabels"}
            size="small"
            bold={true}
          />
        )}

        {hasNets && (
          <CommonButton
            title={`Nets Tab (${selectedVariable.Nets.length})`}
            label={`Nets (${selectedVariable.Nets.length})`}
            onClick={() => setActiveTab("nets")}
            variant="tab"
            isSelected={activeTab === "nets"}
            size="small"
            bold={true}
          />
        )}

        <CommonButton
          title="Custom Rows Tab"
          label={selectedVariable?.customRows?.length ? `Custom Rows (${selectedVariable.customRows.length})` : "Custom Rows"}
          onClick={() => setActiveTab("customRows")}
          variant="tab"
          isSelected={activeTab === "customRows"}
          size="small"
          bold={true}
        />

        <CommonButton
          title="VarNames Tab — the underlying SPSS variable(s) mapped to this question, each with optional row/col/stat"
          label={selectedVariable?.varName?.length ? `VarNames (${selectedVariable.varName.length})` : "VarNames"}
          onClick={() => setActiveTab("varNames")}
          variant="tab"
          isSelected={activeTab === "varNames"}
          size="small"
          bold={true}
        />

        {specialCfg && (
          <CommonButton
            title={`${specialCfg.label} Tab`}
            label={`${specialCfg.label} (${(selectedVariable[specialCfg.prop] || []).length})`}
            onClick={() => setActiveTab("specialRows")}
            variant="tab"
            isSelected={activeTab === "specialRows"}
            size="small"
            bold={true}
          />
        )}

        {hasValues && (
          <CommonButton
            title="Row Calculations Tab"
            label={selectedVariable?.RowCalcs?.length ? `Row Calcs (${selectedVariable.RowCalcs.length})` : "Row Calcs"}
            onClick={() => setActiveTab("rowCalcs")}
            variant="tab"
            isSelected={activeTab === "rowCalcs"}
            size="small"
            bold={true}
          />
        )}
      </div>

      {selectedVariable?.tb && (
        <div className="tabulation-syntax-display">
          <h3>Generated Syntax</h3>
          <pre>{selectedVariable.tb}</pre>
        </div>
      )}

      <div className="tab-content">{renderTabContent()}</div>

      {selectedVariable && (
        <div className="action-buttons">
          <CommonButton
            title="Save Changes"
            label={loading ? "Saving..." : "Save Changes"}
            onClick={saveChanges}
            variant="primary"
            size="large"
            loading={loading}
            bold={true}
          />

          {selectedVariable?.varName?.length > 0 && (
            <CommonButton
              title="Re-fetch this variable's data from the SPSS file and update the database"
              label={refreshLoading ? "Refreshing..." : "Refresh from SPSS"}
              onClick={handleRefreshFromSpss}
              variant="secondary"
              size="large"
              loading={refreshLoading}
              bold={true}
            />
          )}

          <CommonButton
            title="Change this question's type — split grids, factor 2D→3D, switch Single/Numeric/Multiple, or convert to Custom/Stacked/Volume/Share"
            label="Change Type"
            onClick={() => setConvertOpen(true)}
            variant="secondary"
            size="large"
            bold={true}
          />

          <CommonButton
            title="Duplicate this question (the copy can then be converted to another type)"
            label="Duplicate"
            onClick={handleDuplicate}
            variant="secondary"
            size="large"
            bold={true}
          />

          {convSnapshot && (
            <CommonButton
              title="Revert the last type conversion / duplicate"
              label="Undo Conversion"
              onClick={undoConversion}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}


          {selectedVariable.type &&
            (selectedVariable.type.includes("2DGrid") ||
             selectedVariable.type.includes("3DGrid") ||
             selectedVariable.type.includes("Grid")) && (
            <CommonButton
              title="Swap Rows and Columns"
              label="Swap Rows/Cols"
              onClick={handleSwapRowsAndCols}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {selectedVariable.type === 'Single2DGrid' && (
            <CommonButton
              title="Convert to Custom — one custom row per value, with logic OR-ing that value across every grid varName (e.g. Var1=1 or Var2=1 or Var3=1). Values will be removed."
              label="OE Combo"
              onClick={handleOECombo}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {(selectedVariable?.type === 'Custom' || (selectedVariable?.customRows?.length > 0)) && (
            <CommonButton
              title={
                selectedVariable?.type === 'Custom'
                  ? "Convert Custom rows to Multiple2DGrid — creates binary Yes/No SPSS variables for each custom row with logic"
                  : selectedVariable?.type === 'Multiple2DGrid'
                    ? "Merge custom rows into this Multiple2DGrid — appends new rows and SPSS variables to the existing ones"
                    : selectedVariable?.type === 'Single2DGrid'
                      ? "Write each custom row's code into the data of every grid variable where its condition is met, and append the value label — no new variables are created"
                      : `Create a new "${selectedKey}new" Multiple2DGrid variable from custom rows, removing custom rows from the original`
              }
              label={loading ? "Saving..." : "Save Variable"}
              onClick={handleSaveCustomVariable}
              variant="primary"
              size="large"
              loading={loading}
              bold={true}
            />
          )}

          {loading && <Loader loading={loading} />}
        </div>
      )}

      <ConvertTypeModal
        isOpen={convertOpen}
        onClose={() => setConvertOpen(false)}
        selectedKey={selectedKey}
        variable={selectedVariable}
        dataGroup={dataGroup}
        onApply={applyConversionPatch}
      />
      {/* {JSON.stringify(selectedVariable)} */}
    </div>
  );
};

export default PropertiesComponent;